import * as React from 'react'
import { ArrowDown, ArrowUp, ChevronsUpDown, SearchX } from 'lucide-react'

import { cn } from '../../../lib/utils'
import { Skeleton } from '../../ui/Skeleton'
import { DataTablePagination } from './Pagination'
import type { Column, TableState } from './types'

interface DataTableProps<T> {
  columns: readonly Column<T>[]
  /** Exactly the rows to paint — already searched, sorted and paginated. */
  rows: readonly T[]
  /** Row count after search and filter, before pagination. Drives the pager. */
  total: number
  state: TableState
  onStateChange: (patch: Partial<TableState>) => void
  rowKey: (row: T) => string
  loading?: boolean
  /** Replaces the built-in "nothing matched" panel. */
  empty?: React.ReactNode
  /** Trailing per-row control, typically a "…" menu. */
  rowActions?: (row: T) => React.ReactNode
  /** Makes the whole row a click target, as the design's player list does. */
  onRowClick?: (row: T) => void
  /** Minimum width before the grid starts scrolling horizontally. */
  minWidth?: number
  /** Hide the footer pager for a small, fixed-size list. Defaults to shown. */
  showPagination?: boolean
  className?: string
}

function defaultCell<T>(row: T, column: Column<T>): React.ReactNode {
  if (column.cell) return column.cell(row)
  const value = (row as Record<string, unknown>)[column.key]
  return value == null ? null : String(value)
}

/**
 * Read-only list with sortable headers and optional row actions. CSS grid
 * rather than <table> so the header and every row share one set of column
 * tracks while the whole thing scrolls horizontally as a unit.
 *
 * Fully controlled: it paints the rows it is handed and reports state changes
 * upward, never sorting or paginating anything itself. That is what lets the
 * same component sit in front of client fixtures here and a Rails controller
 * in roundhouse-rails.
 *
 * The search-and-filter toolbar lives above the card, on the page — see
 * <DataTableToolbar>.
 */
export function DataTable<T>({
  columns,
  rows,
  total,
  state,
  onStateChange,
  rowKey,
  loading,
  empty,
  rowActions,
  onRowClick,
  minWidth = 920,
  showPagination = true,
  className,
}: DataTableProps<T>) {
  const template = [...columns.map((column) => column.width ?? '1fr'), rowActions && '44px']
    .filter(Boolean)
    .join(' ')

  const showEmpty = !loading && rows.length === 0

  return (
    <div
      className={cn(
        'border-border bg-bg-elev overflow-hidden rounded-xl border shadow-2xs',
        className,
      )}
    >
      <div className="scrollbar-x">
        <div style={{ minWidth }}>
          <div
            className="border-border-subtle bg-bg-faint grid h-10 items-center gap-x-4 border-b px-5"
            style={{ gridTemplateColumns: template }}
          >
            {columns.map((column) => {
              const active = state.sortKey === column.key
              const Arrow = !active ? ChevronsUpDown : state.sortDir === 'asc' ? ArrowUp : ArrowDown

              return (
                <button
                  key={column.key}
                  type="button"
                  disabled={!column.sortable}
                  onClick={() =>
                    onStateChange({
                      sortKey: column.key,
                      sortDir: active && state.sortDir === 'asc' ? 'desc' : 'asc',
                      page: 1,
                    })
                  }
                  className={cn(
                    't-label group flex w-full min-w-0 items-center gap-1.5 transition-colors duration-100 ease-out',
                    column.align === 'end' && 'justify-end',
                    column.sortable ? 'hover:text-fg' : 'cursor-default',
                    active && 'text-fg',
                  )}
                >
                  <span className="truncate">{column.header}</span>
                  {column.sortable && (
                    <Arrow
                      className={cn(
                        'size-3 shrink-0 transition-opacity duration-100 ease-out',
                        active ? 'text-fg opacity-100' : 'opacity-0 group-hover:opacity-60',
                      )}
                    />
                  )}
                </button>
              )
            })}
            {rowActions && <span />}
          </div>

          {loading &&
            Array.from({ length: state.pageSize }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="border-border-subtle grid h-(--height-row) items-center gap-x-4 border-t px-5"
                style={{ gridTemplateColumns: template }}
              >
                {columns.map((column) => (
                  <Skeleton key={column.key} className="h-3 w-full max-w-32" />
                ))}
                {rowActions && <span />}
              </div>
            ))}

          {!loading &&
            rows.map((row) => (
              <div
                key={rowKey(row)}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onRowClick(row)
                        }
                      }
                    : undefined
                }
                className={cn(
                  'border-border-subtle grid min-h-(--height-row) items-center gap-x-4 border-t px-5 text-base transition-colors duration-100 ease-out',
                  onRowClick &&
                    'hover:bg-bg-subtle focus-visible:bg-bg-subtle cursor-pointer outline-none',
                )}
                style={{ gridTemplateColumns: template }}
              >
                {columns.map((column) => (
                  <span
                    key={column.key}
                    className={cn(
                      'min-w-0 truncate',
                      column.align === 'end' && 'text-right tabular-nums',
                    )}
                  >
                    {defaultCell(row, column)}
                  </span>
                ))}
                {rowActions && (
                  /* The row is a click target; a menu inside it must not also
                     trigger navigation. */
                  <span
                    className="flex justify-end"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {rowActions(row)}
                  </span>
                )}
              </div>
            ))}

          {showEmpty && (
            <div className="border-border-subtle border-t">
              {empty ?? (
                <div className="px-4 py-14 text-center">
                  <SearchX className="text-fg-subtle mx-auto mb-2 size-5" />
                  <p className="text-fg-muted m-0 text-sm">Nothing matches your filters.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showPagination && (
        <DataTablePagination
          state={state}
          onStateChange={onStateChange}
          total={total}
          className="border-border-subtle bg-bg-faint border-t px-5 py-3"
        />
      )}
    </div>
  )
}

export type { DataTableProps }
